"use client";

import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowRight, Phone } from "lucide-react";
import Link from "next/link";
import Image from "next/image";

export default function GalleryClientPage() {
  const [activeFilter, setActiveFilter] = useState("Alla");

  const categories = [
    "Alla",
    "Installation",
    "Service & Underhåll",
    "Produkter",
    "Stora Projekt",
    "Svetsning",
  ];

  const galleryItems = [
    {
      id: 1,
      title: "Traverskran Installation",
      category: "Installation",
      image:
        "/images/bildgalleri/Lyfthjalp-av-proffs-med-speciallyft-i-tranga-utrymmen.webp",
      description:
        "Installation av 10-tons traverskran i produktionsanläggning",
    },
    {
      id: 2,
      title: "Telfer Service",
      category: "Service & Underhåll",
      image: "/images/produkter/Misia-10ton-lintelfer.webp",
      description: "Regelbundet underhåll av all typer av telflar",
    },
    {
      id: 3,
      title: "Lyftab Lättlastsystem",
      category: "Produkter",
      image: "/images/produkter/Lyftab-lattlastsystem-spv-8met-1000kg.webp",
      description: "Installation av Lyftab lättlastsystem",
    },
    {
      id: 4,
      title: "Specialupphängning Skena",
      category: "Stora Projekt",
      image: "/images/bildgalleri/Special-upphangning-aluminium-skena.webp",
      description: "Specialupphängning av aluminium skena",
    },
    {
      id: 5,
      title: "Krankomponent Reparation",
      category: "Installation",
      image: "/images/bildgalleri/Renovering-av-special-gaffelvagn.webp",
      description: "Reparation och förstärkning av krankomponenter",
    },
    {
      id: 6,
      title: "Svetsarbete Kranbanor",
      category: "Svetsning",
      image:
        "/images/bildgalleri/Montering-av-kranbanan-och-2tons-travers.webp",
      description: "Kvalificerad svetsning av kranbalkar",
    },
    {
      id: 7,
      title: "Radiosystem Installation",
      category: "Service & Underhåll",
      image: "/images/bildgalleri/2st-Juuko-radiostyrning-2hast.webp",
      description: "Installation av radiosystem för kranstyrning",
    },
    {
      id: 8,
      title: "Kran Montage",
      category: "Installation",
      image: "/images/bildgalleri/Pelarsvangkran-1000kg.webp",
      description: "Montage av svängkran med 270° rotation",
    },
    {
      id: 9,
      title: "Specialtillverkning",
      category: "Svetsning",
      image: "/images/bildgalleri/Bockkran-500kg.webp",
      description: "Specialtillverkad krankomponent enligt kundspecifikation",
    },
  ];

  const filteredItems =
    activeFilter === "Alla"
      ? galleryItems
      : galleryItems.filter((item) => item.category === activeFilter);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="py-20 gradient-hero-medium">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
              Kranprojekt &{" "}
              <span className="text-blue-600">Installationer</span> i Sverige
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              Se våra genomförda projekt och installationer. Från små
              reparationer till stora industriprojekt - vi levererar kvalitet i
              varje uppdrag.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Badge
                variant="secondary"
                className="px-4 py-2 bg-blue-600 text-white border border-gray-200"
              >
                9 Projekt
              </Badge>
              <Badge
                variant="secondary"
                className="px-4 py-2 bg-blue-600 text-white border border-gray-200"
              >
                Över hela Sverige
              </Badge>
              <Badge
                variant="secondary"
                className="px-4 py-2 bg-blue-600 text-white border border-gray-200"
              >
                2023-2024
              </Badge>
            </div>
          </div>
        </div>
      </section>

      {/* Filter Buttons */}
      <section className="py-12 bg-white border-b border-gray-200">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-4">
            {categories.map((category) => (
              <Button
                key={category}
                onClick={() => setActiveFilter(category)}
                variant={activeFilter === category ? "default" : "outline"}
                className={`px-6 py-2 rounded-full transition-all duration-200 ${
                  activeFilter === category
                    ? "bg-blue-600 text-white border-blue-600 shadow-md"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-600"
                }`}
              >
                {category}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredItems.map((item) => (
              <Card
                key={item.id}
                className="group hover:border-blue-200 transition-all duration-300 bg-white border border-gray-200 overflow-hidden"
              >
                <div className="relative aspect-[4/3] overflow-hidden">
                  <Image
                    src={item.image || "/placeholder.svg"}
                    alt={item.title}
                    fill
                    sizes="(max-width: 768px) 100vw, 33vw"
                    className="object-cover object-center group-hover:scale-105 transition-transform duration-300"
                    style={{ objectPosition: "center 40%" }}
                  />
                  <Badge className="absolute top-4 left-4 bg-blue-600 text-white border-0">
                    {item.category}
                  </Badge>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-semibold mb-2 text-gray-900 group-hover:text-blue-600 transition-colors">
                    {item.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {item.description}
                  </p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Imponerad av Våra Projekt?
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto text-blue-100">
            Kontakta oss för att diskutera ditt nästa kranprojekt. Vi hjälper
            dig från idé till färdig installation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center items-center">
            <Link
              href="/kontakta-oss"
              className="inline-flex items-center justify-center gap-2 bg-white text-blue-600 px-8 py-4 rounded-xl font-semibold text-lg hover:bg-blue-50 transition-all duration-200 min-h-[56px] border-2 border-white shadow-lg hover:shadow-xl no-underline"
            >
              Få Kostnadsfri Offert
              <ArrowRight className="h-5 w-5" />
            </Link>
            <Link
              href="tel:0709380356"
              className="inline-flex items-center justify-center gap-2 bg-transparent text-white px-8 py-4 rounded-xl font-semibold text-lg border-2 border-white hover:bg-white hover:text-blue-600 transition-all duration-200 min-h-[56px] shadow-lg hover:shadow-xl no-underline"
            >
              <Phone className="h-5 w-5" />
              Ring: 070-938 03 56
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
